import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button, Message } from '@getro/rombo';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from 'components/atoms/Card';
import { GoogleIcon } from 'components/atoms/googleSignInButton/components/googleIcon';
import { Zap, Contact, MoreVertical, Trash } from 'lucide-react';
import { IntegrationStatus } from './IntegrationStatus';
import RemoveGoogleAccount from './RemoveGoogleAccount';

export const GoogleIntegrationCard = ({
  googleSync: {
    email,
    syncStatus: status = 'not_connected',
    scopes = [],
    contactsSynced = 0,
    lastSyncedAt = null,
  } = {},
  onConnect,
  onReconnect,
  onRemoveConfirm,
  isRemoving,
  variant = 'default',
}) => {
  const [showRemove, setShowRemove] = useState(false);
  const isOnboarding = variant === 'onboarding';

  const grantedScopes = scopes || [];
  const hasContactsScope =
    grantedScopes.includes('https://www.googleapis.com/auth/contacts.readonly') ||
    grantedScopes.includes('https://www.googleapis.com/auth/contacts.other.readonly');
  return (
    <Flex
      sx={{
        gap: '16px',
        p: '16px',
        border: '1px solid',
        borderColor: '#dee4ed',
        borderRadius: '4px',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '600px',
      }}
    >
      <Flex sx={{ gap: '16px' }} alignItems="flex-start" width="100%">
        {/* Icon column */}
        <Box width="32px" height="32px" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <GoogleIcon width="32px" height="32px" />
        </Box>

        {/* Content column */}
        <Flex flexDirection="column" sx={{ gap: '8px' }} flex={1}>
          {/* Header row: title/email and actions */}
          <Flex alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
            <Text
              color="text.dark"
              fontWeight="500"
              fontSize="14px"
              sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {email || 'Google contacts sync'}
            </Text>
            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
              {status !== 'not_connected' && <IntegrationStatus status={status} />}
              {status === 'not_connected' && (
                <Button size="small" onClick={onConnect} variant="accent">
                  Connect
                </Button>
              )}
              {!isOnboarding && status !== 'not_connected' && (
                <Box
                  sx={{
                    minWidth: '24px',
                    maxHeight: '24px',
                    height: '24px',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    alignItems: 'center',
                  }}
                  tx="dataTable"
                  data-testid="row-action"
                  display="flex"
                >
                  <Dropdown
                    placement="right-start"
                    trigger={
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        width="24px"
                        height="24px"
                        sx={{ cursor: 'pointer', svg: { color: 'text.main' } }}
                        aria-label="More actions"
                      >
                        <Box as={MoreVertical} size="16px" aria-hidden="true" />
                      </Flex>
                    }
                  >
                    <Card
                      sx={{ cursor: 'pointer', width: '228px', gap: '4px' }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Flex
                        sx={{
                          gap: '8px',
                          py: '4px',
                          color: 'text.error',
                          '&:hover': { color: 'text.error' },
                        }}
                        alignItems="center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRemove(true);
                        }}
                      >
                        <Box as={Trash} size="16px" aria-hidden="true" />
                        <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                          Remove
                        </Text>
                      </Flex>
                    </Card>
                  </Dropdown>
                </Box>
              )}
            </Flex>
          </Flex>

          {/* Body: description and sync details */}
          <Text color="text.subtle" fontSize="14px" lineHeight="1.4">
            Sync your Google contacts to Getro and decide which networks can access them.
          </Text>
          {status !== 'not_connected' && (
            <Flex flexDirection="column" sx={{ gap: '8px', mt: '16px' }}>
              {hasContactsScope && (
                <>
                  <Flex sx={{ gap: '8px', alignItems: 'center', color: 'text.main' }}>
                    <Box as={Contact} width="16px" height="16px" aria-hidden="true" />
                    <Text color="text.main" fontSize="14px">
                      {contactsSynced} contacts synced
                    </Text>
                  </Flex>
                  <Flex sx={{ gap: '8px', alignItems: 'center', color: 'text.main' }}>
                    <Box as={Zap} width="16px" height="16px" aria-hidden="true" />
                    {status === 'syncing' ? (
                      <Text color="text.main" fontSize="14px">
                        Sync in progress, this may take up to 3 hours.
                      </Text>
                    ) : (
                      <Text color="text.main" fontSize="14px">
                        {`Last synced on ${
                          lastSyncedAt
                            ? new Date(lastSyncedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '-'
                        }`}
                      </Text>
                    )}
                  </Flex>
                </>
              )}
              {status === 'permissions_missing' && (
                <Message
                  type="warning"
                  content={
                    <Text>
                      We need more permissions,{' '}
                      <Box
                        as="button"
                        onClick={onReconnect}
                        sx={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          color: 'inherit',
                        }}
                      >
                        reconnect your Google account
                      </Box>{' '}
                      and make sure you grant all permissions requested.
                    </Text>
                  }
                />
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      {showRemove && (
        <RemoveGoogleAccount
          isSubmitting={isRemoving}
          onCancel={() => setShowRemove(false)}
          onConfirm={async () => {
            await onRemoveConfirm?.();
            setShowRemove(false);
          }}
        />
      )}
    </Flex>
  );
};

GoogleIntegrationCard.propTypes = {
  googleSync: PropTypes.object,
  onConnect: PropTypes.func.isRequired,
  onReconnect: PropTypes.func,
  onRemoveConfirm: PropTypes.func,
  isRemoving: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'onboarding']),
};

GoogleIntegrationCard.defaultProps = {
  googleSync: null,
  onReconnect: undefined,
  onRemoveConfirm: undefined,
  isRemoving: false,
  variant: 'default',
};
